import _ from 'lodash';
import scribe from '@grab-x/scribe-web-sdk';

import ScribeEventName from 'constants/scribe';
import { targetUserTypes } from 'data/trident-types';

// getCountryPercentageInSegment returns the percentage of a country in the segment's country breakdown
export function getCountryPercentageInSegment(segmentData, countryName) {
  const countryBreakdown = _.get(segmentData, 'pax:country.attributes', []);
  const userCounts = _.map(countryBreakdown, x => x.value);
  const totalUserCount = _.sum(userCounts);
  const countryUserCount = _.get(
    _.find(countryBreakdown, x => x.name === countryName),
    'value',
    0
  );

  if (totalUserCount > 0) {
    return (countryUserCount * 100.0) / totalUserCount;
  }
  return 0;
}

export const userTypeAppModeMap = {
  passenger: 'pax',
  driver: 'dax',
  foodmax: 'mex',
  mexusers: 'mex',
  userservice: 'mex',
  moveit_passenger: 'pax',
  moveit_driver: 'dax',
  ovo: 'mex',
};

export type SegmentParams = {
  segmentName: string,
  countryPercentageWarningThreshold: number,
  featureFlagConfig: any,
  targetUserType: string,
  countryID: string,
  campaignID: string,
  differentCountryPercent: number,
  countryBreakDownAvailable: boolean,
  segmentSize: number,
};

function isInvalidSegmentName(segmentName) {
  /* Should return false if segment is valid */
  if (typeof segmentName !== 'string') return true;
  return !segmentName.match(/^[a-zA-Z][a-zA-Z0-9\-_ ]{1,117}[a-zA-Z0-9\-_]$/i);
}

// doSegmentValidation returns the error check of a selected segment name.
export function doSegmentValidation(params: SegmentParams) {
  const {
    segmentName,
    countryPercentageWarningThreshold,
    featureFlagConfig,
    targetUserType,
    countryID,
    campaignID,
    differentCountryPercent,
    countryBreakDownAvailable,
    segmentSize,
  } = params;
  const mandateCountryBreakdown = _.get(featureFlagConfig, 'enforceSegmentInsight', false);
  const seg = segmentName;
  if (_.isEmpty(seg)) {
    return 'Segment cannot be empty';
  }
  const segSections = seg.split('.');
  if (targetUserType === targetUserTypes.dax || targetUserType === targetUserTypes.moveITDax) {
    let segmentNamespace = '';
    let segmentID = '';
    if (segSections.length === 1) {
      segmentID = segSections[0];
    } else if (segSections.length === 2) {
      segmentNamespace = segSections[0];
      segmentID = segSections[1];
    } else {
      return 'Segment input is invalid';
    }

    if (targetUserType === targetUserTypes.moveITDax && segSections.length !== 2) {
      return 'Segment for MOVEIT_DRIVER user type should be of the format moveit.segmentID eg: moveit.5345345';
    }

    // segmentID validations
    if (_.isNaN(Number(segmentID))) {
      return 'Driver segment ID must be a number';
    }
    if (Number(segmentID) <= 0) {
      return 'Driver segment ID must be a positive number';
    }
    if (segmentID.indexOf(' ') >= 0) {
      return 'Driver segment ID must not have leading or trailing whitespaces';
    }

    // segmentNamespace validations
    if (segSections.length === 2) {
      if (isInvalidSegmentName(segmentNamespace)) {
        return 'Segment namespace is invalid';
      }
      if (targetUserType === targetUserTypes.moveITDax && segmentNamespace !== 'moveit') {
        return 'Segment namespace for MOVEIT_DRIVER user type should be moveit';
      }
    }
  } else {
    const segmentationName = segSections[segSections.length - 1];
    if (isInvalidSegmentName(segmentationName)) {
      return 'Segment name is invalid';
    }
  }

  const sendCountryCheckBlockedEvent = () => {
    scribe.sendEvent(ScribeEventName.SEGMENT_COUNTRY_CHECK_BLOCKED, {
      campaignID,
      countryID,
      countryBreakDownAvailable,
      differentCountryPercent,
      segmentSize,
    });
  };

  if (mandateCountryBreakdown && !countryBreakDownAvailable) {
    sendCountryCheckBlockedEvent();
    return 'Segment without insight data is NOT allowed to be used. Try refreshing the insights in SegP';
  }

  if (differentCountryPercent > countryPercentageWarningThreshold) {
    sendCountryCheckBlockedEvent();
    return `The segment has more than ${countryPercentageWarningThreshold}% users not in the campaign's target country!`;
  }

  return '';
}
